import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listDeliveryCommitmentRules } from "@/lib/delivery-commitment-rules.functions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle } from "lucide-react";

/**
 * Sprint 15 — Delivery Commitments Foundation. Admin stub.
 * Table + CRUD exist; no evaluator reads rows this sprint. Forward-locked
 * for D-Enforcement.
 */
export const Route = createFileRoute("/_authenticated/app/admin/shipping-policies")({
  component: ShippingPoliciesPage,
});

function ShippingPoliciesPage() {
  const listFn = useServerFn(listDeliveryCommitmentRules);
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["delivery-commitment-rules"],
    queryFn: () => listFn(),
  });

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold">Delivery Commitments</h1>
        <p className="text-sm text-muted-foreground">
          Policies that defend delivery dates against carrier transit time.
          Foundation ships the data model and rate-shop Estimated Delivery
          Date. Enforcement (warn / block / auto-upgrade) and commitment
          signals ship next.
        </p>
      </header>

      <Card className="border-warning/30 bg-warning/5">
        <CardContent className="py-3 text-xs flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 text-warning shrink-0" />
          <div>
            <strong>Preview.</strong> Rules can be stored, but no evaluator
            reads them this sprint. They become enforceable in D-Enforcement
            without re-modeling.
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3"><CardTitle className="text-sm">Rules</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <p className="text-xs text-muted-foreground py-4">Loading…</p>
          ) : rules.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4">
              No rules configured. Default behavior is to compute and persist
              Estimated Delivery Dates without commitment enforcement.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Scope</TableHead>
                  <TableHead>Commitment source</TableHead>
                  <TableHead>Enforcement</TableHead>
                  <TableHead>Grace (h)</TableHead>
                  <TableHead>Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r: Record<string, unknown>) => (
                  <TableRow key={String(r.id)}>
                    <TableCell className="text-xs">
                      {r.customer_id ? `Customer ${String(r.customer_id).slice(0, 8)}…` : "Tenant"}
                      {r.channel ? ` · ${String(r.channel)}` : ""}
                    </TableCell>
                    <TableCell className="text-xs">{String(r.commitment_source)}</TableCell>
                    <TableCell><Badge variant="outline">{String(r.enforcement_mode)}</Badge></TableCell>
                    <TableCell className="text-xs tabular-nums">{String(r.grace_hours)}</TableCell>
                    <TableCell className="text-xs">{r.is_active ? "yes" : "no"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}